import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { loadExternalScript } from "../../utils/loadExternalScript";

const DEFAULT_IMAGE_HOST = "https://quiz.my-stylist.pro";
const DEFAULT_DEMO_IMAGE_PATH = "/facemesh_demo.png";
const FACEMESH_SOURCE_PROXY_PATH = "/api/facemesh/source";
const FACEMESH_OVERLAY_UPLOAD_PATH = "/api/facemesh/overlay";
const FACEMESH_WEBHOOK_URL = process.env.REACT_APP_FACEMESH_WEBHOOK_URL || "";
const FACEMESH_CONTINUE_URL = "/faceshape-3month-29";

const MEDIAPIPE_SCRIPTS = [
  "https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js",
  "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js",
];

const PROGRESS_STEPS = [
  { threshold: 10, label: "Analyzing facial geometry" },
  { threshold: 35, label: "Analyzing skin undertone balance" },
  { threshold: 60, label: "Analyzing eye contrast and clarity" },
  { threshold: 85, label: "Analyzing natural hair depth" },
  { threshold: 100, label: "Calibrating your personal color profile" },
];

const ANALYSIS_PROGRESS_DURATION_MS = 7000;
const AUTO_REDIRECT_DELAY_MS = 1000;
const MESH_CONNECTION_DENSITY_RATIO = 0.5;
const ANALYSIS_KEYFRAMES = [
  { time: 0, progress: 1 },
  { time: 900, progress: 10 }, // Mesh appears.
  { time: 1700, progress: 10 }, // Pause for emphasis.
  { time: 3000, progress: 35 }, // Face oval appears.
  { time: 3700, progress: 35 }, // Pause for emphasis.
  { time: 4800, progress: 60 }, // Eyes + irises appear.
  { time: 5400, progress: 60 }, // Pause for emphasis.
  { time: 6300, progress: 85 }, // Eyebrows appear.
  { time: 7000, progress: 98 }, // Lips and near-finish.
];

const normalizeImageUrl = (rawUrl) => {
  if (!rawUrl) return "";
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  if (rawUrl.startsWith("//")) return `https:${rawUrl}`;
  if (rawUrl.startsWith("/")) return `${DEFAULT_IMAGE_HOST}${rawUrl}`;
  return `${DEFAULT_IMAGE_HOST}/${rawUrl}`;
};

const unwrapImageParam = (rawValue) => {
  if (!rawValue) return "";

  const trimmed = String(rawValue).trim();
  if (!trimmed) return "";

  let decoded = trimmed;
  if (/%[0-9a-f]{2}/i.test(trimmed)) {
    try {
      decoded = decodeURIComponent(trimmed);
    } catch {
      decoded = trimmed;
    }
  }

  if (!/[<>]/.test(decoded) && !/href\s*=/.test(decoded)) {
    return decoded;
  }

  try {
    const doc = new DOMParser().parseFromString(decoded, "text/html");
    const anchor = doc.querySelector("a[href]");
    const href = anchor?.getAttribute("href")?.trim();
    if (href) return href;
  } catch {
    // ignore parsing errors and fall back to regex / raw.
  }

  const hrefMatch = decoded.match(/href\s*=\s*["']([^"']+)["']/i);
  if (hrefMatch?.[1]) return hrefMatch[1].trim();

  return decoded;
};

const getAnalysisImageUrl = (normalizedUrl, apiBase = "") => {
  if (!normalizedUrl) return "";

  try {
    const parsedUrl = new URL(normalizedUrl);
    if (parsedUrl.hostname === "quiz.my-stylist.pro") {
      return `${apiBase}${FACEMESH_SOURCE_PROXY_PATH}?url=${encodeURIComponent(parsedUrl.toString())}`;
    }
    return parsedUrl.toString();
  } catch {
    return normalizedUrl;
  }
};

const buildContinueUrl = (baseUrl, search = "") => {
  if (!search) return baseUrl;
  if (baseUrl.includes("?")) {
    return `${baseUrl}&${search.replace(/^\?/, "")}`;
  }
  return `${baseUrl}${search}`;
};

const getProgressLabel = (progress) => {
  for (const step of PROGRESS_STEPS) {
    if (progress <= step.threshold) {
      return step.label;
    }
  }
  return PROGRESS_STEPS[PROGRESS_STEPS.length - 1].label;
};

const getCurrentStepIndex = (progress) => {
  for (let index = 0; index < PROGRESS_STEPS.length; index += 1) {
    if (progress <= PROGRESS_STEPS[index].threshold) {
      return index;
    }
  }
  return PROGRESS_STEPS.length - 1;
};

const getProgressByKeyframes = (elapsedMs) => {
  if (elapsedMs <= ANALYSIS_KEYFRAMES[0].time) {
    return ANALYSIS_KEYFRAMES[0].progress;
  }

  for (let i = 1; i < ANALYSIS_KEYFRAMES.length; i += 1) {
    const previous = ANALYSIS_KEYFRAMES[i - 1];
    const current = ANALYSIS_KEYFRAMES[i];
    if (elapsedMs <= current.time) {
      if (current.time === previous.time) {
        return current.progress;
      }
      const fraction = (elapsedMs - previous.time) / (current.time - previous.time);
      return previous.progress + (current.progress - previous.progress) * fraction;
    }
  }

  return ANALYSIS_KEYFRAMES[ANALYSIS_KEYFRAMES.length - 1].progress;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image failed to load"));
    img.src = src;
  });

const assertImageUrlResolvable = (src) => {
  try {
    new URL(src, window.location.origin);
  } catch {
    throw new Error("Invalid image URL.");
  }
};

const shouldRetryWithDemoImage = (targetImageUrl, error) => {
  if (targetImageUrl === DEFAULT_DEMO_IMAGE_PATH) {
    return false;
  }

  const message = error?.message || "";
  return message === "Invalid image URL." || message === "Image failed to load";
};

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to render overlay image."));
          return;
        }
        resolve(blob);
      },
      "image/webp",
      0.9
    );
  });

const serializeLandmarksCompact = (faceLandmarks) => {
  if (!Array.isArray(faceLandmarks)) return "[]";

  const compactLandmarks = faceLandmarks.map((point = {}) => ({
    x: Number(point.x) || 0,
    y: Number(point.y) || 0,
    z: Number(point.z) || 0,
  }));

  return JSON.stringify(compactLandmarks);
};

const sendFaceMeshWebhook = async ({ imageUrl, dotsText, clientReferenceId = "" }) => {
  if (!FACEMESH_WEBHOOK_URL) return;

  const body = new URLSearchParams();
  body.append("img", imageUrl);
  body.append("desc", dotsText);
  if (clientReferenceId) {
    body.append("client_reference_id", clientReferenceId);
  }

  const response = await fetch(FACEMESH_WEBHOOK_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body: body.toString(),
    keepalive: true,
  });

  if (!response.ok) {
    throw new Error(`FaceMesh webhook failed with status ${response.status}.`);
  }
};

const assertCanvasReadable = (image) => {
  const probe = document.createElement("canvas");
  probe.width = 1;
  probe.height = 1;
  const probeCtx = probe.getContext("2d");
  probeCtx.drawImage(image, 0, 0, 1, 1);
  // This throws SecurityError when image CORS is not allowed.
  probeCtx.getImageData(0, 0, 1, 1);
};

const ensureMediaPipeReady = async () => {
  await Promise.all(MEDIAPIPE_SCRIPTS.map((src) => loadExternalScript(src)));
  if (!window.FaceMesh || !window.drawConnectors) {
    throw new Error("MediaPipe scripts are loaded but API is unavailable.");
  }
};

const detectLandmarks = (image) =>
  new Promise(async (resolve, reject) => {
    try {
      const faceMesh = new window.FaceMesh({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });
      faceMesh.setOptions({
        staticImageMode: true,
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      faceMesh.onResults((results) => {
        const firstFace = results?.multiFaceLandmarks?.[0];
        if (!firstFace) {
          reject(new Error("No face detected on the provided image."));
          return;
        }
        resolve(firstFace);
      });
      await faceMesh.send({ image });
    } catch (error) {
      reject(error);
    }
  });

const FaceMeshFaceShapePage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [savedOverlayUrl, setSavedOverlayUrl] = useState("");
  const [isSavingOverlay, setIsSavingOverlay] = useState(false);
  const [saveOverlayError, setSaveOverlayError] = useState("");
  const [landmarks, setLandmarks] = useState(null);
  const [imageElement, setImageElement] = useState(null);
  const stagedTimerRef = useRef(null);
  const completionTimerRef = useRef(null);
  const progressValueRef = useRef(0);
  const analysisReadyRef = useRef(false);
  const completionStartedRef = useRef(false);
  const autoStartedUrlRef = useRef("");
  const analysisRunIdRef = useRef(0);
  const completedRunIdRef = useRef(0);
  const uploadedRunIdRef = useRef(0);
  const webhookSentRunIdRef = useRef(0);
  const autoRedirectedRunIdRef = useRef(0);
  const autoRedirectTimerRef = useRef(null);
  const reducedMeshConnectionsRef = useRef(null);
  const canvasRef = useRef(null);

  const rawImageUrlParam = searchParams.get("img") || searchParams.get("image") || "";
  const resolvedImageUrlParam = useMemo(() => unwrapImageParam(rawImageUrlParam), [rawImageUrlParam]);
  const clientReferenceId = searchParams.get("client_reference_id") || "";
  const rawApiBase = searchParams.get("apiBase") || "";
  const apiBase = useMemo(() => rawApiBase.replace(/\/+$/, ""), [rawApiBase]);
  const imageUrl = useMemo(
    () => (resolvedImageUrlParam ? normalizeImageUrl(resolvedImageUrlParam) : DEFAULT_DEMO_IMAGE_PATH),
    [resolvedImageUrlParam]
  );
  const analysisImageUrl = useMemo(() => getAnalysisImageUrl(imageUrl, apiBase), [apiBase, imageUrl]);
  const currentStepIndex = useMemo(() => getCurrentStepIndex(progress), [progress]);

  const updateProgress = (value) => {
    const next = Math.max(0, Math.min(100, Math.round(value)));
    progressValueRef.current = next;
    setProgress(next);
  };

  const clearProgressTimers = () => {
    if (stagedTimerRef.current) {
      window.clearInterval(stagedTimerRef.current);
      stagedTimerRef.current = null;
    }
    if (completionTimerRef.current) {
      window.clearInterval(completionTimerRef.current);
      completionTimerRef.current = null;
    }
  };

  useEffect(() => clearProgressTimers, []);

  useEffect(() => {
    return () => {
      if (autoRedirectTimerRef.current) {
        window.clearTimeout(autoRedirectTimerRef.current);
      }
    };
  }, []);

  const getReducedMeshConnections = () => {
    if (reducedMeshConnectionsRef.current) {
      return reducedMeshConnectionsRef.current;
    }

    const fullMeshConnections = window.FACEMESH_TESSELATION;
    if (!fullMeshConnections) return null;

    const meshConnectionsArray = Array.isArray(fullMeshConnections)
      ? fullMeshConnections
      : Array.from(fullMeshConnections);

    const reducedMeshConnections = meshConnectionsArray.filter((_, index) => {
      return index % Math.round(1 / MESH_CONNECTION_DENSITY_RATIO) === 0;
    });

    reducedMeshConnectionsRef.current = reducedMeshConnections;
    return reducedMeshConnections;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageElement) return;

    const ctx = canvas.getContext("2d");
    canvas.width = imageElement.naturalWidth;
    canvas.height = imageElement.naturalHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

    if (!landmarks || status === "idle") return;

    const draw = window.drawConnectors;
    if (!draw) return;

    if (progress >= 10) {
      const reducedMeshConnections = getReducedMeshConnections() || window.FACEMESH_TESSELATION;
      draw(ctx, landmarks, reducedMeshConnections, { color: "#FFFFFF", lineWidth: 1 });
    }
    if (progress >= 35) {
      draw(ctx, landmarks, window.FACEMESH_FACE_OVAL, { color: "#FACC15", lineWidth: 3 });
    }
    if (progress >= 60) {
      draw(ctx, landmarks, window.FACEMESH_LEFT_EYE, { color: "#22D3EE", lineWidth: 2 });
      draw(ctx, landmarks, window.FACEMESH_RIGHT_EYE, { color: "#22D3EE", lineWidth: 2 });
      draw(ctx, landmarks, window.FACEMESH_LEFT_IRIS, { color: "#22D3EE", lineWidth: 2 });
      draw(ctx, landmarks, window.FACEMESH_RIGHT_IRIS, { color: "#22D3EE", lineWidth: 2 });
    }
    if (progress >= 85) {
      draw(ctx, landmarks, window.FACEMESH_LEFT_EYEBROW, { color: "#22C55E", lineWidth: 2 });
      draw(ctx, landmarks, window.FACEMESH_RIGHT_EYEBROW, { color: "#22C55E", lineWidth: 2 });
    }
    if (progress >= 95) {
      draw(ctx, landmarks, window.FACEMESH_LIPS, { color: "#EF4444", lineWidth: 2 });
    }
  }, [imageElement, landmarks, progress, status]);

  const tryCompleteProgress = () => {
    if (completionStartedRef.current) return;
    if (!analysisReadyRef.current) return;
    if (progressValueRef.current < 98) return;

    completionStartedRef.current = true;
    completionTimerRef.current = window.setInterval(() => {
      const nextProgress = Math.min(progressValueRef.current + 1, 100);
      updateProgress(nextProgress);
      if (nextProgress >= 100) {
        window.clearInterval(completionTimerRef.current);
        completionTimerRef.current = null;
        completedRunIdRef.current = analysisRunIdRef.current;
        setStatus("done");
      }
    }, 15);
  };

  const startProgressAnimation = () => {
    clearProgressTimers();
    analysisReadyRef.current = false;
    completionStartedRef.current = false;
    updateProgress(1);

    const animationStartAt = Date.now();
    stagedTimerRef.current = window.setInterval(() => {
      const elapsed = Math.min(Date.now() - animationStartAt, ANALYSIS_PROGRESS_DURATION_MS);
      updateProgress(getProgressByKeyframes(elapsed));

      if (elapsed >= ANALYSIS_PROGRESS_DURATION_MS) {
        window.clearInterval(stagedTimerRef.current);
        stagedTimerRef.current = null;
        tryCompleteProgress();
      }
    }, 40);
  };

  const runAnalysis = async (targetImageUrl) => {
    const runId = analysisRunIdRef.current + 1;
    analysisRunIdRef.current = runId;

    if (!targetImageUrl) {
      setErrorMessage("Missing image URL. Open /facemesh-faceshape?img=https://quiz.my-stylist.pro/image/your-file.jpeg");
      setStatus("error");
      return;
    }

    try {
      setStatus("analyzing");
      setErrorMessage("");
      setLandmarks(null);
      setSavedOverlayUrl("");
      setSaveOverlayError("");
      setIsSavingOverlay(false);
      setProgress(0);
      startProgressAnimation();

      assertImageUrlResolvable(targetImageUrl);
      const [, loadedImage] = await Promise.all([ensureMediaPipeReady(), loadImage(targetImageUrl)]);

      try {
        assertCanvasReadable(loadedImage);
      } catch (_) {
        throw new Error(
          "The image cannot be processed due to CORS restrictions. Add Access-Control-Allow-Origin for https://payment.my-stylist.pro on the image host."
        );
      }

      if (runId !== analysisRunIdRef.current) return;
      setImageElement(loadedImage);
      const detectedLandmarks = await detectLandmarks(loadedImage);
      if (runId !== analysisRunIdRef.current) return;
      setLandmarks(detectedLandmarks);
      analysisReadyRef.current = true;
      tryCompleteProgress();
    } catch (error) {
      if (runId !== analysisRunIdRef.current) return;
      if (shouldRetryWithDemoImage(targetImageUrl, error)) {
        runAnalysis(DEFAULT_DEMO_IMAGE_PATH);
        return;
      }
      clearProgressTimers();
      setStatus("error");
      updateProgress(0);
      setErrorMessage(error?.message || "Face analysis failed.");
    }
  };

  // runAnalysis is intentionally invoked only when a new normalized URL appears.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!imageUrl) {
      analysisRunIdRef.current += 1;
      autoStartedUrlRef.current = "";
      clearProgressTimers();
      setImageElement(null);
      setLandmarks(null);
      setSavedOverlayUrl("");
      setSaveOverlayError("");
      setIsSavingOverlay(false);
      setStatus("error");
      updateProgress(0);
      setErrorMessage("Missing image URL. Open /facemesh-faceshape?img=https://quiz.my-stylist.pro/image/your-file.jpeg");
      return;
    }

    if (autoStartedUrlRef.current === analysisImageUrl) return;
    autoStartedUrlRef.current = analysisImageUrl;
    runAnalysis(analysisImageUrl);
  }, [analysisImageUrl, imageUrl]);

  useEffect(() => {
    if (status !== "done") return;
    if (!canvasRef.current) return;
    if (!completedRunIdRef.current) return;
    if (uploadedRunIdRef.current === completedRunIdRef.current) return;

    let cancelled = false;
    const currentRunId = completedRunIdRef.current;

    const saveOverlay = async () => {
      setIsSavingOverlay(true);
      setSaveOverlayError("");

      try {
        const overlayBlob = await canvasToBlob(canvasRef.current);
        const uploadResponse = await fetch(`${apiBase}${FACEMESH_OVERLAY_UPLOAD_PATH}`, {
          method: "POST",
          headers: { "Content-Type": overlayBlob.type || "image/webp" },
          body: overlayBlob,
        });

        const uploadPayload = await uploadResponse.json();
        if (!uploadResponse.ok) {
          throw new Error(uploadPayload?.error || "Failed to save overlay image.");
        }

        if (cancelled) return;
        setSavedOverlayUrl(uploadPayload.url || "");
        uploadedRunIdRef.current = currentRunId;

        if (uploadPayload.url && webhookSentRunIdRef.current !== currentRunId) {
          webhookSentRunIdRef.current = currentRunId;
          const dotsText = serializeLandmarksCompact(landmarks);
          sendFaceMeshWebhook({
            imageUrl: uploadPayload.url,
            dotsText,
            clientReferenceId,
          }).catch((webhookError) => {
            console.error("FaceMesh webhook error:", webhookError);
          });
        }
      } catch (error) {
        if (cancelled) return;
        setSaveOverlayError(error?.message || "Failed to save overlay image.");
      } finally {
        if (!cancelled) {
          setIsSavingOverlay(false);
        }
      }
    };

    saveOverlay();

    return () => {
      cancelled = true;
    };
  }, [apiBase, clientReferenceId, landmarks, status]);

  useEffect(() => {
    if (status !== "done") return;
    const currentRunId = completedRunIdRef.current;
    if (!currentRunId) return;
    if (autoRedirectedRunIdRef.current === currentRunId) return;

    autoRedirectedRunIdRef.current = currentRunId;
    autoRedirectTimerRef.current = window.setTimeout(() => {
      autoRedirectTimerRef.current = null;
      const nextUrl = buildContinueUrl(FACEMESH_CONTINUE_URL, window.location.search);
      window.location.assign(nextUrl);
    }, AUTO_REDIRECT_DELAY_MS);
  }, [status]);

  const handleContinue = () => {
    const nextUrl = buildContinueUrl(FACEMESH_CONTINUE_URL, window.location.search);
    window.location.assign(nextUrl);
  };

  return (
    <div className="min-h-screen bg-[#F2ECE3] px-4 py-6 pb-28 funnel-cs-font-body text-[#2B2622] sm:px-6 sm:py-8 sm:pb-8">
      <div className="mx-auto w-full max-w-[700px] rounded-[28px] border border-[#EAD9CD] bg-white p-4 shadow-[var(--funnel-cs-shadow-card)] sm:p-8">
        <h1 className="text-center funnel-cs-font-heading text-xl font-semibold leading-snug text-[#2B2622] sm:text-3xl">
          For accurate analysis,
          <br />
          we use advanced AI technology
        </h1>

        {(status === "analyzing" || status === "done") && (
          <div className="mt-4 rounded-[18px] border border-[#EAD9CD] bg-[#FBF8F3] p-3 sm:mt-6 sm:p-5">
            <div className="flex items-end justify-between">
              <p className="text-xs font-semibold text-[#9A2E1A] sm:text-sm">Analysis progress</p>
              <p className="text-2xl font-semibold funnel-cs-font-heading leading-none text-[#2B2622] sm:text-3xl">{progress}%</p>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#EAD9CD] sm:mt-3 sm:h-2.5">
              <div
                className="h-full rounded-full bg-[#9A2E1A] transition-all duration-150"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-2 text-left text-sm font-semibold text-[#2B2622] sm:mt-3 sm:text-base">
              {getProgressLabel(progress)}
            </p>
            <div className="mt-3 space-y-1.5 sm:mt-4 sm:space-y-2">
              {PROGRESS_STEPS.map((step, index) => {
                const isCompleted = progress >= step.threshold;
                const isActive = index === currentStepIndex && status !== "done";
                return (
                  <div key={step.label} className="flex items-center gap-2 sm:gap-2.5">
                    <span
                      className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10px] font-bold transition-colors sm:h-5 sm:w-5 sm:text-xs ${
                        isCompleted
                          ? "bg-[#9A2E1A] text-[#FBF8F3]"
                          : isActive
                          ? "bg-[#F3E2DB] text-[#9A2E1A]"
                          : "bg-[#F6F1EB] text-[#8A8075]"
                      }`}
                    >
                      {isCompleted ? "✓" : index + 1}
                    </span>
                    <span
                      className={`text-xs sm:text-sm ${
                        isCompleted || isActive ? "font-medium text-[#2B2622]" : "text-[#8A8075]"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 overflow-hidden rounded-[18px] border border-[#EAD9CD] bg-[#FBF8F3] shadow-sm sm:mt-6">
          {imageElement ? (
            <div className="flex max-h-[42vh] items-center justify-center sm:max-h-none">
              <canvas ref={canvasRef} className="block h-auto max-h-[42vh] w-auto max-w-full sm:max-h-none sm:w-full" />
            </div>
          ) : (
            <div className="flex aspect-[3/4] max-h-[42vh] items-center justify-center px-6 text-center text-sm font-medium text-[#6F665D] sm:max-h-none">
              Preparing your AI analysis...
            </div>
          )}
        </div>

        {status === "done" && (
          <div className="mt-4 rounded-[14px] border border-[#EAD9CD] bg-[#FBF8F3] p-3 text-sm text-[#6F665D]">
            {isSavingOverlay && <p>Securing your analysis preview...</p>}
            {!isSavingOverlay && savedOverlayUrl && <p>Your analysis preview is ready.</p>}
            {!isSavingOverlay && !savedOverlayUrl && <p>Your analysis is complete.</p>}
          </div>
        )}

        {status === "error" && (
          <div className="mt-5 rounded-[14px] border border-[#E5C9BD] bg-[#F3E2DB] p-3 text-sm text-[#762514]">
            {errorMessage}
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#EAD9CD] bg-[#FBF8F3]/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 backdrop-blur-sm sm:px-6">
        <div className="mx-auto w-full max-w-[700px]">
          <button
            type="button"
            onClick={handleContinue}
            disabled={status !== "done"}
            className="inline-flex h-12 w-full items-center justify-center rounded-[14px] bg-[#9A2E1A] px-6 text-base font-bold text-[#FBF8F3] transition-colors duration-200 hover:bg-[#762514] focus:outline-none focus:ring-2 focus:ring-[#9A2E1A] focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#EAD9CD] disabled:text-[#8A8075]"
          >
            {status === "done" ? "Continue" : "Finalizing analysis..."}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaceMeshFaceShapePage;
